<template>
  <modal-component
    :is-displayed="isAddToOrderModalOpened"
    :is-full-screen-modal="true"
    :remove-outside-click="true"
    :title="$t('WorkOrders.AddToWorkOrderTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <delete-modal
      :is-deleting="isDeleteModalOpened"
      @cancel-delete="isDeleteModalOpened = false"
      @toggle-modal="isDeleteModalOpened = false"
      @confirm-delete="openConfirmationModal('delete')"
    />
    <confirmation-modal
      :delete-text="confirmationModalText"
      :is-confirmation-modal-opened="isConfirmationModalOpened"
      @confirm-action="confirmationModalAction"
      @close-confirmation-modal="isConfirmationModalOpened = false"
      @toggle-modal="isConfirmationModalOpened = false"
    />
    <refurbish-modal
      v-if="isRefurbishModalOpened"
      :mac="mac"
      :imei="imei"
      :idType="idType"
      :workOrderId="workOrderId"
      :refresh-data="getWorkOrderDevices"
      @toggle-modal="isRefurbishModalOpened = false"
    />
    <move-device-modal
      v-if="isMoveModalOpened"
      :mac="mac"
      :imei="imei"
      :idType="idType"
      :workOrderId="workOrderId"
      :refresh-data="getWorkOrderDevices"
      @toggle-modal="isMoveModalOpened = false"
    />
    <div class="px-6">
      <div class="flex w-1/2 gap-8">
        <!--<input-field v-model="mac" label="MAC" />-->
        <text-box-mask
          :label="$t('ICDManagement.MAC')"
          :vModel="mac"
          :maskRules="rules"
          :inputAttr="{
            'aria-label': 'Mask',
            style: 'text-transform: uppercase',
          }"
          :showClearButton="false"
          mask="XX:XX:XX:XX:XX:XX"
          maskInvalidMessage="Inserted address does not match a mac address."
          valueChangeEvent="keyup"
          @value-changed="onValueChange"
        />
        <input-field v-model="imei" label="IMEI" />
      </div>
      <div v-if="isLoading" class="flex w-full justify-center">
        <small-loader />
      </div>
      <custom-table
        v-else
        :force-refresh="true"
        :is-data-source-loading="isDataSourceLoading"
        :table-data="workOrderDevices"
        :table-headers="tableHeaders"
        :delete-work-order-column="true"
        :is-action-list-not-displayed="true"
        @selected-rows="getSelectedRows"
        @delete-work-order="toggleDeleteModal"
        @force-refresh="getWorkOrderDevices"
      />
    </div>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter, formatMacAddress, isString } from "@/core/Services/GlobalFunctions";
import DeviceService from "@/modules/Devices/Services/DeviceService";
import WorkOrderService from "@/modules/WorkOrders/Services/WorkOrderService";
import DeleteModal from "@/core/Components/Notifications/DeleteModal.vue";
import ConfirmationModal from "@/core/Components/Notifications/ConfirmationModal.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import { WorkOrderDevice } from "@/modules/WorkOrders/Services/WorkOrderTypes";
import TextBoxMask from "@/core/Components/FormElements/TextBoxMask.vue";
import { required } from "@vuelidate/validators";
import useValidate from "@vuelidate/core";
import RefurbishModal from "@/modules/WorkOrders/Components/RefurbishModal.vue";
import MoveDeviceModal from "./MoveDeviceModal.vue";

export default defineComponent({
  components: {
    SmallLoader,
    ConfirmationModal,
    DeleteModal,
    CustomTable,
    InputField,
    ModalComponent,
    TextBoxMask,
    RefurbishModal,
    MoveDeviceModal
  },
  props: {
    isAddToOrderModalOpened: {
      type: Boolean,
      required: true,
    },
    workOrderId: {
      type: Number,
      required: true,
    },
    refreshData: Function,
  },
  emits: ["toggle-modal", "delete-work-order"],
  data() {
    return {
      deviceService: new DeviceService(),
      workOrderService: new WorkOrderService(),
      workOrderDevices: [] as WorkOrderDevice[],
      selectedDevice: {} as WorkOrderDevice,
      toast: useToast(),
      v$: useValidate(),
      confirmationModalText: "",
      isConfirmationModalOpened: false,
      isDeleteModalOpened: false,
      isRefurbishModalOpened: false,
      isMoveModalOpened: false,
      isDataSourceLoading: false,
      isLoading: false,
      mac: "",
      imei: "",
      idType: "",
      tableHeaders: {
        "device.name": { header: "WorkOrders.Name", visible: true },
        "device.mac": { header: "MAC", visible: true },
        lastSync: { header: "WorkOrders.LastSync", visible: true },
        refurbished: { header: "WorkOrders.Refurbished", visible: true },
      },
      rules: {
        X: /[0-9A-Fa-f]/,
      },
    };
  },
  validations() {
    return {
      mac: {
        required,
      },
    };
  },

  async created() {
    this.isLoading = true;
    try {
      await this.getWorkOrderDevices();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },

  async unmounted() {
    await this.refreshData?.();
  },

  watch: {
    async mac(value) {
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
      if (macRegex.test(value)) {
        await this.getDeviceByIdentifier({
          identifier: value,
          type: "mac",
        });
      } else {
        console.log("Invalid MAC");
      }
    },
    async imei(value) {
      const imeiRegex = /^\d{15}$/;
      if (imeiRegex.test(value)) {
        await this.getDeviceByIdentifier({
          identifier: value,
          type: "imei",
        });
      } else {
        console.log("Invalid IMEI");
      }
    },
  },

  methods: {
    async getWorkOrderDevices() {
      this.isDataSourceLoading = true;
      try {
        this.workOrderDevices = await this.workOrderService.getWorkOrderDevices({
          workOrderId: this.workOrderId
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async getDeviceByIdentifier({ identifier, type }: { identifier: string | number; type: "mac" | "imei" }) {
      this.isDataSourceLoading = true;
      try {
        await this.workOrderService.addDeviceToWorkOrderWithIdentifier({
          identifiers: [identifier],
          identifierType: type,
          workOrderId: this.workOrderId,
        });
        this.toast.success((this as any).$t("WorkOrders.AddToWorkOrderSuccess"));
        await this.getWorkOrderDevices();
      } catch (error: any) {
        if(!error.response.data.errors){
          this.toast.warning((this as any).$t("WorkOrders.NoDevice"));
        } else if(
          error.response.data.errors["Identifiers[0]"] && 
          (error.response.data.errors["Identifiers[0]"][0] == 'Device is not refurbished' || 
          error.response.data.errors["Identifiers[0]"][0] == 'Identifier is already in use!')
        ){
          this.idType = type;
          this.isRefurbishModalOpened = true;
          this.toast.warning((this as any).$t("WorkOrders.DeviceExists"));
        } else if(error.response.data.errors[""] && error.response.data.errors[""][0] == 'Identifier is already in non applied WorkOrder!'){
          this.idType = type;
          this.isMoveModalOpened = true;
          this.toast.warning(ErrorMessageFormatter(error));
        } else {
          this.toast.error(ErrorMessageFormatter(error));
        }
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async deleteDeviceFromWorkOrder() {
      try {
        await this.workOrderService.deleteWorkOrderDevice(this.selectedDevice.id);
        this.toast.success((this as any).$t("WorkOrders.RemoveFromWorkOrderSuccess"));
        this.isDeleteModalOpened = false;
        await this.getWorkOrderDevices();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
      }
    },
    getSelectedRows(values: WorkOrderDevice[]) {
      if (values.length) {
        this.selectedDevice = values[0];
      }
    },
    async confirmationModalAction() {
      switch (this.confirmationModalText) {
        case "delete":
          await this.deleteDeviceFromWorkOrder();
          break;
      }
      this.isConfirmationModalOpened = false;
    },
    openConfirmationModal(key: string) {
      this.isConfirmationModalOpened = true;
      this.confirmationModalText = key;
    },
    toggleDeleteModal() {
      this.isDeleteModalOpened = !this.isDeleteModalOpened;
    },
    onValueChange(e: any) {
      try {
        const previousValue = e.previousValue;
        const newValue = e.value;

        this.mac =
          previousValue !== newValue
            ? formatMacAddress(newValue).toUpperCase()
            : formatMacAddress(previousValue).toUpperCase();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        throw error;
      }
    },
  },
});
</script>

<style scoped>
/*.dx-invalid-message > .dx-overlay-content {
  color: #fff !important;
  background-color: #d9534f !important;
  border-radius: 0px !important;
}*/
</style>
